import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Field } from '@/components/ui/Field';
import {
  describedByIds,
  FIELD_LEADING_ICON,
  FIELD_TRAILING_ICON,
  fieldControlClass,
  fieldIconData,
} from '@/components/ui/fieldClasses';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    label,
    error,
    hint,
    id,
    required,
    leadingIcon,
    trailingIcon,
    'aria-describedby': describedBy,
    ...props
  },
  ref,
) {
  const inputId = id ?? props.name;
  const hintId = inputId ? `${inputId}-hint` : undefined;
  const errorId = inputId ? `${inputId}-error` : undefined;

  const control = (
    <div
      className="relative"
      {...fieldIconData({
        leading: Boolean(leadingIcon),
        trailing: Boolean(trailingIcon),
      })}
    >
      {leadingIcon ? <span className={FIELD_LEADING_ICON}>{leadingIcon}</span> : null}
      <input
        id={inputId}
        ref={ref}
        required={required}
        className={fieldControlClass({ error: Boolean(error), className })}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={
          inputId
            ? describedByIds({
                id: inputId,
                error: Boolean(error),
                hint: Boolean(hint),
                extra: describedBy,
              })
            : describedBy
        }
        {...props}
        {...fieldIconData({
          leading: Boolean(leadingIcon),
          trailing: Boolean(trailingIcon),
        })}
      />
      {trailingIcon ? <span className={FIELD_TRAILING_ICON}>{trailingIcon}</span> : null}
    </div>
  );

  if (!label && !error && !hint) return control;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
    >
      {control}
    </Field>
  );
});
